package fr.frux.core.maps

import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.calculateEndPadding
import androidx.compose.foundation.layout.calculateStartPadding
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.hypot
import kotlin.math.pow

/** Circonference de la Terre a l'equateur, en metres. Etalon du Web Mercator. */
private const val EARTH_CIRCUMFERENCE_METERS = 40_075_016.686

/**
 * Rayon, en metres, du disque circonscrit a ce que la camera montre.
 *
 * Calcule ici plutot que lu sur la vue rendue du SDK : cette formule est pure, elle rend la meme
 * valeur sur les quatre cibles, elle est testable sans emulateur, et elle repond avant le premier
 * rendu de la carte (le SDK n'expose sa projection qu'une fois une surface attachee).
 *
 * Le disque circonscrit sur-couvre les coins du rectangle d'environ 20 %. C'est voulu : mieux vaut
 * charger un peu au-dela du cadre que laisser un bord vide.
 *
 * Rend 0 tant que la vue n'est pas mesuree — l'appelant doit alors s'abstenir de conclure quoi que
 * ce soit, et surtout pas requeter a rayon nul.
 */
fun visibleRadiusMeters(
    camera: MapCameraPosition,
    viewport: DpSize,
): Double {
    if (viewport.width.value <= 0f || viewport.height.value <= 0f) return 0.0

    val metersPerDp =
        EARTH_CIRCUMFERENCE_METERS *
            cos(camera.target.latitude * PI / 180) /
            (TILE_SIZE_DP * 2.0.pow(camera.zoom.toDouble()))

    val halfDiagonalDp = hypot(viewport.width.value / 2.0, viewport.height.value / 2.0)

    return metersPerDp * halfDiagonalDp
}

/**
 * Marge ajoutee de chaque cote du rectangle visible envoye au serveur (#621), en fraction de la
 * largeur ou de la hauteur de la vue. Un petit pan ne doit pas decouvrir une bande vide : la
 * pastille « Rechercher dans cette zone » ne s'arme qu'au-dela d'un quart du rayon charge, soit
 * environ 14 % de la hauteur sur un telephone en portrait, en deca de cette marge.
 */
const val VISIBLE_BOUNDS_MARGIN_FRACTION = 0.2

/**
 * Rectangle geographique que la camera montre, elargi de [marginFraction] de chaque cote.
 *
 * Complement de [visibleRadiusMeters] : le disque circonscrit sur-couvre les coins du rectangle,
 * et sur un telephone en portrait pres de la moitie d'un lot trie par distance tombe hors ecran.
 * Envoye au serveur avec le rayon, le rectangle rend cette part au visible.
 *
 * Meme choix d'une formule pure que [visibleRadiusMeters], et meme pente de Mercator que
 * [MapBounds.toCameraPosition]. Rend `null` tant que la vue n'est pas mesuree. Les bords sont
 * bornes au monde : une vue planetaire rend le rectangle entier, pas un rectangle invalide.
 */
fun visibleBounds(
    camera: MapCameraPosition,
    viewport: DpSize,
    marginFraction: Double = VISIBLE_BOUNDS_MARGIN_FRACTION,
): MapBounds? {
    if (viewport.width.value <= 0f || viewport.height.value <= 0f) return null

    val worldDp = TILE_SIZE_DP * 2.0.pow(camera.zoom.toDouble())
    val halfWidthDp = viewport.width.value * (1 + 2 * marginFraction) / 2
    val halfHeightDp = viewport.height.value * (1 + 2 * marginFraction) / 2

    val halfSpanLongitude = halfWidthDp / worldDp * 360.0
    val halfSpanY = halfHeightDp / worldDp * 2 * PI
    val centerY = mercatorY(camera.target.latitude)

    return MapBounds(
        southwest =
            Location(
                latitude = inverseMercatorY(centerY - halfSpanY),
                longitude = (camera.target.longitude - halfSpanLongitude).coerceAtLeast(-MAX_LONGITUDE),
            ),
        northeast =
            Location(
                latitude = inverseMercatorY(centerY + halfSpanY),
                longitude = (camera.target.longitude + halfSpanLongitude).coerceAtMost(MAX_LONGITUDE),
            ),
    )
}

private const val MAX_LONGITUDE = 180.0

/**
 * Point geographique situe a [offset] du centre de la camera (x vers l'est, y vers le sud), en Web
 * Mercator et avec la meme pente que [visibleBounds]. Sert a lire sous l'epingle du mode visee, qui
 * n'est pas forcement au centre de la vue.
 */
fun locationAtOffset(
    camera: MapCameraPosition,
    offset: DpOffset,
): Location {
    val worldDp = TILE_SIZE_DP * 2.0.pow(camera.zoom.toDouble())
    val y = mercatorY(camera.target.latitude) - offset.y.value / worldDp * 2 * PI
    return Location(
        latitude = inverseMercatorY(y),
        longitude = camera.target.longitude + offset.x.value / worldDp * 360.0,
    )
}

/**
 * Inverse exact de [locationAtOffset] : decalage, par rapport au centre de la camera, ou tombe
 * [location] a l'ecran (x vers l'est, y vers le sud). Sert a dessiner le cercle de borne du mode
 * visee autour d'un point geographique.
 */
fun offsetOfLocation(
    camera: MapCameraPosition,
    location: Location,
): DpOffset {
    val worldDp = TILE_SIZE_DP * 2.0.pow(camera.zoom.toDouble())
    val x = (location.longitude - camera.target.longitude) / 360.0 * worldDp
    val y = (mercatorY(camera.target.latitude) - mercatorY(location.latitude)) / (2 * PI) * worldDp
    return DpOffset(x.toFloat().dp, y.toFloat().dp)
}

/** Longueur a l'ecran, en dp, de [meters] a [latitude] et [zoom] (meme etalon que [visibleRadiusMeters]). */
fun metersToDp(
    meters: Double,
    latitude: Double,
    zoom: Float,
): Dp {
    val metersPerDp =
        EARTH_CIRCUMFERENCE_METERS * cos(latitude * PI / 180) / (TILE_SIZE_DP * 2.0.pow(zoom.toDouble()))
    return (meters / metersPerDp).toFloat().dp
}

/**
 * Decalage de l'epingle de visee par rapport au centre de la vue : centre de la zone que le chrome
 * ne couvre pas (colonne de 400 dp en grand ecran, barres haute et basse).
 */
fun aimPinOffset(
    insets: PaddingValues,
    layoutDirection: LayoutDirection,
): DpOffset =
    DpOffset(
        x = (insets.calculateStartPadding(layoutDirection) - insets.calculateEndPadding(layoutDirection)) / 2,
        y = (insets.calculateTopPadding() - insets.calculateBottomPadding()) / 2,
    )
